import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { POLL } from '@/lib/pollTiers'
import { groupWaitingDollies, type WaitingDollyGroup, type WaitingDollyRow } from '@/lib/runsRegister'

/**
 * F02 UF-09: the berry New Run's "released dollies with no run yet". Reads the
 * RELEASED cooling batches (the Tipping board's read, with the delivery's grower)
 * and the ACTIVE runs, then groups what no active run matches. A failed or
 * pending read is reported as such, never as an empty list.
 */
export function useWaitingDollies(enabled: boolean): {
  groups: WaitingDollyGroup[]; loading: boolean; failed: boolean
} {
  const tenantId = useActiveTenantId()
  const q = useQuery({
    queryKey: ['waiting-dollies', tenantId],
    enabled: enabled && !!tenantId,
    refetchInterval: POLL.live,
    queryFn: async () => {
      // Paged, never one over-wide range: a cut-off read would hide a waiting group.
      const [dollies, runs] = await Promise.all([
        fetchAllPaged<any>((from, to) => supabase.from('cooling_batches')
          .select('batch_id, variety, delivery:deliveries(grower, grower_id)')
          .eq('tenant_id', tenantId!).eq('status', 'RELEASED').order('batch_id').range(from, to)),
        fetchAllPaged<{ grower: string | null; variety: string | null }>((from, to) => supabase.from('runs')
          .select('run_id, grower, variety')
          .eq('tenant_id', tenantId!).eq('status', 'ACTIVE').is('deleted_at', null).order('run_id').range(from, to)),
      ])
      if (dollies.error) throw new Error(dollies.error)
      if (runs.error) throw new Error(runs.error)
      const rows: WaitingDollyRow[] = dollies.rows.map((r: any) => {
        const d = Array.isArray(r.delivery) ? r.delivery[0] : r.delivery
        return { grower: d?.grower ?? null, grower_id: d?.grower_id ?? null, variety: r.variety ?? null }
      })
      return groupWaitingDollies(rows, runs.rows)
    },
  })
  return { groups: q.data ?? [], loading: enabled && (q.isPending || !tenantId), failed: q.isError }
}
